// chart.js theme from the design tokens (docs/design/branding-kit.html §Graphiques):
// Inter, recessive hairline grid, bars ≤ 24 px with 4 px rounded value ends,
// 2 px lines, white tooltip. Series colours follow the validated fixed order.
import { BarController, BarElement, Legend, LineElement, PointElement, Tooltip } from 'chart.js';

const token = (name, fallback) => {
  if (typeof window === 'undefined' || !window.getComputedStyle) return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

const SERIES_FALLBACK = ['#5B4BDB', '#EB6834', '#1BAF7A', '#EDA100', '#E87BA4', '#008300', '#2A78D6', '#E34948'];

// Colour of series n (0-based). Never cycle: a 9th series folds into « Autres ».
// An HTML legend uses .u-swatch--s{n + 1}, bound to the same token.
export const seriesColor = (index) => token(`--u-series-${index + 1}`, SERIES_FALLBACK[index] || SERIES_FALLBACK[0]);

// The fill of an area under a line: the series hue at ~10 % (.u-swatch--soft).
export const seriesWash = (index) => `${seriesColor(index)}1A`;

// Call once with the Chart class a page registers (import { Chart } from 'chart.js').
// It registers what it themes first: chart.js only creates the defaults of an
// element, controller or plugin when it is registered (setting them before
// throws), and a later registration would overwrite values set ahead of it.
// Registering twice is a no-op, so pages keep registering what they draw.
export const applyChartTheme = (Chart) => {
  Chart.register(BarController, BarElement, LineElement, PointElement, Legend, Tooltip);
  const d = Chart.defaults;
  d.font.family = token('--font-family', 'Inter, sans-serif');
  d.font.size = 12;
  d.color = token('--text-secondary', '#565470');
  d.borderColor = token('--u-chart-grid', '#ECEBF3');
  d.elements.bar.borderRadius = 4;
  d.elements.bar.borderSkipped = 'start';
  // maxBarThickness is a dataset option in chart.js 4 (an element default is ignored).
  d.datasets.bar.maxBarThickness = 24;
  d.elements.line.borderWidth = 2;
  d.elements.line.tension = 0.25;
  d.elements.point.radius = 0;
  d.elements.point.hoverRadius = 4;
  // Legends are HTML (.u-legend + .u-swatch) in the kit: a canvas legend is
  // opt-in per chart (options.plugins.legend.display = true), as it was for
  // every page that did not register the plugin.
  d.plugins.legend.display = false;
  d.plugins.legend.labels.boxWidth = 8;
  d.plugins.legend.labels.boxHeight = 8;
  d.plugins.legend.labels.usePointStyle = true;
  d.plugins.legend.labels.pointStyle = 'rectRounded';
  Object.assign(d.plugins.tooltip, {
    backgroundColor: token('--bg-surface', '#FFFFFF'),
    titleColor: token('--text-primary', '#16142A'),
    bodyColor: token('--text-secondary', '#565470'),
    borderColor: token('--border-subtle', '#E9E8F1'),
    borderWidth: 1,
    padding: 10,
    cornerRadius: 10,
    boxPadding: 4,
    usePointStyle: true,
  });
};
